import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtINR } from "../lib/constants";
import {
  Building2, Briefcase, FileCheck2, Award, Factory, ShieldCheck, Leaf, FileText,
  AlertTriangle, ListTodo, Plus, UploadCloud, History, CalendarClock, ClipboardSignature,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  Legend, LineChart, Line, CartesianGrid,
} from "recharts";

const GREEN = "#72BE44", DARK = "#145353", GREY = "#58595B";
const INK = "var(--brand-ink)";
const PIE_COLORS = [GREEN, "#2E8B84", "#F59E0B", "#0EA5E9", "#8B5CF6", "#F43F5E", "#A8A29E"];

const KPI = ({ icon: Icon, label, value, sub, to, tone = "green", testid }) => {
  const nav = useNavigate();
  const tones = {
    green: { bg: "rgba(114,190,68,0.12)", fg: GREEN },
    dark: { bg: "rgba(20,83,83,0.12)", fg: "var(--brand-ink)" },
    amber: { bg: "rgba(245,158,11,0.12)", fg: "#B45309" },
    rose: { bg: "rgba(244,63,94,0.1)", fg: "#BE123C" },
    sky: { bg: "rgba(14,165,233,0.12)", fg: "#0369A1" },
  }[tone];
  return (
    <button type="button" onClick={() => to && nav(to)} data-testid={testid}
      className="text-left bg-white border border-stone-200 rounded-xl p-4 hover:shadow-[0_8px_24px_rgba(20,83,83,0.1)] hover:-translate-y-0.5 transition-all duration-200 w-full">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: tones.bg }}>
          <Icon className="w-4.5 h-4.5" style={{ color: tones.fg, width: 18, height: 18 }} strokeWidth={1.9} />
        </div>
        <div className="min-w-0">
          <div className="text-2xl font-bold tracking-tight text-stone-900 leading-none">{value}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mt-1 truncate">{label}</div>
        </div>
      </div>
      {sub && <div className="text-[11px] text-stone-400 mt-2">{sub}</div>}
    </button>
  );
};

const Card = ({ title, children, action }) => (
  <div className="bg-white border border-stone-200 rounded-xl p-5">
    <div className="flex items-center justify-between mb-4">
      <div className="text-sm font-bold" style={{ color: INK }}>{title}</div>
      {action}
    </div>
    {children}
  </div>
);

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [mgmt, setMgmt] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/dashboard/enterprise").then((r) => setData(r.data)).catch(console.error);
    if (["super_admin", "hod", "director"].includes(user?.role)) {
      api.get("/admin/dashboard-kpis").then((r) => setMgmt(r.data)).catch(() => {});
    }
    try { setRecent(JSON.parse(localStorage.getItem("recent_projects") || "[]").slice(0, 5)); } catch { /* ignore */ }
  }, [user]);

  const canCreate = (mod) => user?.role === "super_admin" || !!user?.perms?.[mod]?.create;

  if (!data) return <div className="text-stone-500">Loading dashboard...</div>;
  const { kpis, charts, my } = data;
  const isAssociate = ["senior_associate", "junior_associate"].includes(user?.role);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold mb-1" style={{ color: GREEN }}>PTESPL · ECMP Operations</p>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-stone-900">
            {user?.role === "director" ? "Business Overview" : user?.role === "hod" ? "Department Overview" : isAssociate ? `Welcome, ${user?.name?.split(" ")[0]}` : "Operations Overview"}
          </h1>
          <p className="text-stone-500 text-sm mt-1">Live data across clients, EC, consents, compliance and tasks.</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-stone-400">Today</div>
          <div className="font-semibold text-stone-800 text-sm">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
        </div>
      </div>

      {/* Quick actions (hidden for read-only roles) */}
      {(canCreate("leads") || canCreate("projects") || canCreate("proposals") || canCreate("tasks") || canCreate("documents")) && (
        <div className="flex flex-wrap gap-2" data-testid="quick-actions">
          {canCreate("leads") && <QuickAction icon={Building2} label="New Client / Lead" onClick={() => nav("/leads")} />}
          {canCreate("projects") && <QuickAction icon={Briefcase} label="Add Project" onClick={() => nav("/projects")} />}
          {canCreate("proposals") && <QuickAction icon={FileText} label="Create Proposal" onClick={() => nav("/proposals")} />}
          {canCreate("tasks") && <QuickAction icon={ListTodo} label="Assign Task" onClick={() => nav("/tasks")} />}
          {canCreate("documents") && <QuickAction icon={UploadCloud} label="Upload Document" onClick={() => nav("/projects")} />}
        </div>
      )}

      {/* Personal strip for associates */}
      {isAssociate && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="my-work-strip">
          <KPI icon={ListTodo} label="My Open Tasks" value={my.open_tasks} to="/tasks" tone="dark" testid="kpi-my-tasks" />
          <KPI icon={AlertTriangle} label="My Overdue" value={my.overdue_tasks} to="/tasks" tone={my.overdue_tasks ? "rose" : "green"} testid="kpi-my-overdue" />
          <KPI icon={Briefcase} label="Active Projects" value={kpis.total_projects} to="/projects" testid="kpi-my-projects" />
          <KPI icon={CalendarClock} label="Compliance Due" value={kpis.six_monthly_pending} to="/compliance" tone="amber" testid="kpi-my-compliance" />
        </div>
      )}

      {/* KPI Grid */}
      <section data-testid="kpi-grid">
        <div className="text-[10px] uppercase tracking-[0.22em] text-stone-400 font-bold mb-2">Key Performance Indicators · click to open</div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          <KPI icon={Building2} label="Total Active Clients" value={kpis.active_clients} to="/clients" tone="dark" testid="kpi-active-clients" />
          <KPI icon={Leaf} label="Total EC Projects" value={kpis.ec_projects} to="/projects" testid="kpi-ec-projects" />
          <KPI icon={FileCheck2} label="EC Under Process" value={kpis.ec_under_process} to="/projects" tone="amber" testid="kpi-ec-process" />
          <KPI icon={Award} label="EC Obtained" value={kpis.ec_obtained} to="/projects" testid="kpi-ec-obtained" />
          <KPI icon={Factory} label="MPCB CTE" value={`${kpis.cte_pending} / ${kpis.cte_obtained}`} sub="Pending / Obtained" to="/compliance" tone="sky" testid="kpi-cte" />
          <KPI icon={ShieldCheck} label="MPCB CTO" value={`${kpis.cto_pending} / ${kpis.cto_obtained}`} sub="Pending / Obtained" to="/compliance" tone="sky" testid="kpi-cto" />
          <KPI icon={Briefcase} label="ECMP Active Projects" value={kpis.ecmp_active} to="/projects" tone="dark" testid="kpi-ecmp-active" />
          <KPI icon={CalendarClock} label="Six-Monthly Pending" value={kpis.six_monthly_pending} to="/compliance" tone="amber" testid="kpi-six-monthly" />
          <KPI icon={Leaf} label="PMR Pending" value={kpis.pmr_pending} to="/trackers/pmr" tone="amber" testid="kpi-pmr" />
          <KPI icon={FileText} label="Proposals Awaiting" value={kpis.proposals_awaiting} to="/proposals" tone="sky" testid="kpi-proposals-awaiting" />
          <KPI icon={AlertTriangle} label="Overdue Tasks" value={kpis.overdue_tasks} to="/tasks" tone={kpis.overdue_tasks ? "rose" : "green"} testid="kpi-overdue-tasks" />
          <KPI icon={ListTodo} label="Open Tasks" value={kpis.open_tasks} to="/tasks" testid="kpi-open-tasks" />
        </div>
      </section>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="charts-row-1">
        <Card title="Project Status Overview">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={charts.status} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                {charts.status.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip /><Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card title="EC vs MPCB Consent Progress">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={charts.ec_mpcb} barSize={26}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip /><Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="pending" name="Pending" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              <Bar dataKey="obtained" name="Obtained" fill={GREEN} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Monthly Project Progress">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={charts.monthly}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip /><Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="new_projects" name="New Projects" stroke={INK} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="milestones_done" name="Milestones Done" stroke={GREEN} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="charts-row-2">
        <Card title="Client-wise Project Distribution">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={charts.clients} layout="vertical" barSize={12} margin={{ left: 10 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="projects" name="Projects" fill={INK} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Department-wise Task Allocation">
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={charts.departments} dataKey="tasks" nameKey="name" outerRadius={80}>
                {charts.departments.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip /><Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Pending vs Completed Activities">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={charts.activities} barSize={22}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e5e4" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip /><Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="pending" name="Pending" stackId="a" fill="#F59E0B" radius={[0, 0, 0, 0]} />
              <Bar dataKey="completed" name="Completed" stackId="a" fill={GREEN} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Deadlines + Recently viewed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card title="Upcoming Compliance Deadlines" action={<Link to="/calendar" className="text-xs font-semibold hover:underline" style={{ color: GREEN }}>Open calendar →</Link>}>
            <div className="divide-y divide-stone-100" data-testid="upcoming-deadlines">
              {charts.deadlines.length === 0 && <div className="py-6 text-center text-sm text-stone-400">No upcoming compliance deadlines 🎉</div>}
              {charts.deadlines.map((d, i) => (
                <button key={i} onClick={() => d.project_id ? nav(`/projects/${d.project_id}`) : nav("/compliance")}
                  className="w-full text-left py-2.5 flex items-center gap-3 hover:bg-stone-50 px-2 rounded transition">
                  <span className="text-[9px] uppercase font-bold px-2 py-1 rounded-full shrink-0" style={{ background: "rgba(114,190,68,0.12)", color: DARK }}>{d.type}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-stone-800 truncate">{d.title}</div>
                    <div className="text-[11px] text-stone-400 truncate">{d.project}</div>
                  </div>
                  <div className="text-xs font-bold shrink-0" style={{ color: DARK }}>{d.due_date}</div>
                </button>
              ))}
            </div>
          </Card>
        </div>
        <Card title="Recently Viewed Projects" action={<History className="w-4 h-4 text-stone-300" />}>
          <div className="space-y-1.5" data-testid="recently-viewed">
            {recent.length === 0 && <div className="py-6 text-center text-sm text-stone-400">Open a project and it will appear here</div>}
            {recent.map((r) => (
              <Link key={r.id} to={`/projects/${r.id}`} className="block px-3 py-2.5 rounded-lg border border-stone-100 hover:border-[#72BE44] hover:bg-[#72BE44]/5 transition">
                <div className="text-sm font-medium text-stone-800 truncate">{r.name}</div>
                <div className="text-[11px] text-stone-400 truncate">{r.client || ""}</div>
              </Link>
            ))}
          </div>
        </Card>
      </div>

      {/* Super admin: funnel + workload + finance (existing management KPIs) */}
      {mgmt && (
        <section data-testid="management-kpis" className="space-y-4">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: GREEN }}>Management · Conversion & Finance</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI icon={Briefcase} label="Booked Revenue (WO)" value={fmtINR(mgmt.booked_revenue)} to="/work-orders" tone="dark" testid="kpi-booked-revenue" />
            <KPI icon={AlertTriangle} label="Outstanding Payments" value={fmtINR(mgmt.outstanding_total)} to="/invoices" tone="amber" testid="kpi-outstanding" />
            <KPI icon={ClipboardSignature} label="Pipeline Revenue" value={fmtINR(mgmt.pipeline_revenue)} to="/proposals" tone="sky" testid="kpi-pipeline" />
            <KPI icon={AlertTriangle} label="Delayed Projects" value={mgmt.delayed_count} to="/projects?delayed=1" tone={mgmt.delayed_count ? "rose" : "green"} testid="kpi-delayed" />
          </div>
          <Card title="Team Workload" action={<span className="text-[10px] text-stone-400">click a row for member's projects</span>}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="team-workload">
                <thead className="text-[10px] uppercase tracking-wider text-stone-500">
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-2 font-semibold">Member</th>
                    <th className="text-left py-2 font-semibold">Role</th>
                    <th className="text-right py-2 font-semibold">Projects</th>
                    <th className="text-right py-2 font-semibold">Leads</th>
                    <th className="text-right py-2 font-semibold">Open Milestones</th>
                  </tr>
                </thead>
                <tbody>
                  {mgmt.team_workload.map((w) => (
                    <tr key={w.id} data-testid={`workload-row-${w.id}`} onClick={() => nav(`/projects?assigned=${w.id}`)}
                      className="border-b border-stone-100 hover:bg-emerald-50/40 cursor-pointer transition">
                      <td className="py-2 font-medium" style={{ color: DARK }}>{w.name}</td>
                      <td className="py-2 text-xs text-stone-500">{w.role}</td>
                      <td className="py-2 text-right font-bold" style={{ color: GREEN }}>{w.projects}</td>
                      <td className="py-2 text-right">{w.leads}</td>
                      <td className="py-2 text-right">{w.milestones > 5 ? <span className="text-rose-600 font-bold">{w.milestones}</span> : w.milestones}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      )}
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick }) {
  return (
    <button onClick={onClick} data-testid={`quick-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="flex items-center gap-2 bg-white border border-stone-200 px-3.5 py-2 rounded-full text-xs font-semibold text-stone-700 hover:border-[#72BE44] hover:text-[#145353] hover:shadow-sm transition">
      <Plus className="w-3 h-3" style={{ color: "#72BE44" }} />
      <Icon className="w-3.5 h-3.5" style={{ color: "#145353" }} /> {label}
    </button>
  );
}
